import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Subject, Subscription, fromEvent, merge, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalDatabaseService, SyncOperation } from './local-database.service';
import { v4 as uuidv4 } from 'uuid';

export interface SyncConflict {
  url: string;
  method: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class NetworkSyncService implements OnDestroy {
  public isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  public isSyncing$ = new BehaviorSubject<boolean>(false);
  public pendingItemsCount$ = new BehaviorSubject<number>(0);
  public pendingOps$ = new BehaviorSubject<SyncOperation[]>([]);
  /** Emits whenever a queued operation is rejected with a 409 Conflict. */
  public conflictErrors$ = new Subject<SyncConflict>();
  /** Emits whenever sync is halted due to an expired/invalid session (401/403). */
  public authError$ = new Subject<void>();
  private subscriptions: Subscription = new Subscription();
  private readonly apiUrl = 'http://localhost:3000/api';

  constructor(
    private http: HttpClient,
    private localDb: LocalDatabaseService
  ) {
    // Restore forced-offline state across page reloads
    const forced = localStorage.getItem('force_offline') === 'true';
    if (forced) {
      this.isOnline$.next(false);
    }
    this.initNetworkMonitoring();
    this.updatePendingCount();
  }

  private initNetworkMonitoring() {
    // Listen for online/offline events, but honour the developer force-offline toggle
    this.subscriptions.add(
      merge(
        fromEvent(window, 'online').pipe(map(() => true)),
        fromEvent(window, 'offline').pipe(map(() => false))
      ).subscribe(isOnline => {
        // If the dev toggle has forced us offline, ignore real 'online' events
        if (isOnline && localStorage.getItem('force_offline') === 'true') return;
        this.isOnline$.next(isOnline);
        if (isOnline) {
          this.syncPendingOperations();
        }
      })
    );
     
    // Attempt sync on startup if online
    if (this.isOnline$.value) {
      this.syncPendingOperations();
    }

    // When isOnline$ flips to true (including via the dev toggle), drain the queue
    this.subscriptions.add(
      this.isOnline$.subscribe(isOnline => {
        if (isOnline) {
          this.syncPendingOperations();
        }
      })
    );
  }

  get isOnline(): boolean {
    return this.isOnline$.value;
  }

  /** Resolve once the sync queue is empty AND no sync is in progress.
   * Use after queueOperation() when the caller needs the server to have
   * processed the write before continuing (e.g. before refetching a list
   * to pick up server-assigned IDs).
   *
   * Polls every 50ms; bails out after `timeoutMs` so a wedged sync can't
   * hang the caller indefinitely. */
  async waitForSync(timeoutMs: number = 10000): Promise<void> {
    if (!this.isOnline) return;
    const start = Date.now();
    while (this.isSyncing$.value || (await this.localDb.syncQueue.count()) > 0) {
      if (Date.now() - start > timeoutMs) return;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  private async updatePendingCount() {
    const ops = await this.localDb.syncQueue.orderBy('timestamp').toArray();
    this.pendingItemsCount$.next(ops.length);
    this.pendingOps$.next(ops);
  }

  async queueOperation(
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    payload?: any,
    tempId?: string,
    needsTransactionId?: boolean,
  ) {
    // For PUT/PATCH operations, deduplicate: if a pending entry for the same URL already
    // exists, replace it with the latest payload (last-write-wins on the queue itself).
    if (method === 'PUT' || method === 'PATCH') {
      const existing = await this.localDb.syncQueue
        .where('url').equals(url)
        .and((op) => op.method === method)
        .first();
      if (existing) {
        await this.localDb.syncQueue.update(existing.id!, {
          payload,
          timestamp: Date.now(),
          retryCount: 0,
        });
        await this.updatePendingCount();
        if (this.isOnline) {
          this.syncPendingOperations();
        }
        return;
      }
    }

    const op: SyncOperation = {
      id: uuidv4(),
      method,
      url,
      payload,
      tempId,
      needsTransactionId,
      timestamp: Date.now(),
      retryCount: 0
    };
    await this.localDb.syncQueue.add(op);
    await this.updatePendingCount();
    
    if (this.isOnline) {
      this.syncPendingOperations();
    }
  }

  /**
   * After a POST syncs, extract the server-assigned ID from the response and
   * rewrite any later queued ops (still in-memory batch) that reference the
   * temp ID in their URL or payload.
   */
  private reconcileTempId(
    tempId: string,
    response: any,
    remainingOps: SyncOperation[],
  ): Promise<void>[] {
    const realId = this.extractServerIdFromResponse(response);
    if (!realId || realId === tempId) return [];

    const updates: Promise<void>[] = [];
    for (const futureOp of remainingOps) {
      let changed = false;

      if (futureOp.url.includes(tempId)) {
        futureOp.url = futureOp.url.replaceAll(tempId, realId);
        changed = true;
      }

      if (futureOp.payload) {
        const payloadStr = JSON.stringify(futureOp.payload);
        if (payloadStr.includes(tempId)) {
          futureOp.payload = JSON.parse(payloadStr.replaceAll(tempId, realId));
          changed = true;
        }
      }

      if (changed) {
        updates.push(
          this.localDb.syncQueue
            .update(futureOp.id!, { url: futureOp.url, payload: futureOp.payload })
            .then(() => undefined),
        );
      }
    }
    return updates;
  }

  private extractServerIdFromResponse(response: any): string | null {
    if (!response || typeof response !== 'object') return null;

    // Check common top-level ID field names
    const topLevelKeys = ['_id', 'id', 'boundBookId', 'bound_book_id'];
    for (const key of topLevelKeys) {
      if (response[key] && typeof response[key] === 'string') {
        return response[key];
      }
    }

    // Check one level deep inside nested objects (e.g. { vendor: { _id: '...' } })
    for (const val of Object.values(response)) {
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        const nested = val as Record<string, any>;
        for (const key of ['_id', 'id']) {
          if (nested[key] && typeof nested[key] === 'string') {
            return nested[key];
          }
        }
      }
    }

    return null;
  }

  private async syncPendingOperations() {
    if (this.isSyncing$.value || !this.isOnline) return;
    this.isSyncing$.next(true);

    try {
      const pendingOps = await this.localDb.syncQueue.orderBy('timestamp').toArray();
      
      for (let i = 0; i < pendingOps.length; i++) {
        const op = pendingOps[i];
        if (!this.isOnline) break; // Network lost during sync

        // Drop malformed queued operations so they do not loop forever.
        if (
          !op.url ||
          op.url.includes('/undefined') ||
          /\/null(?:$|[/?#])/.test(op.url)
        ) {
          console.warn(`Discarding malformed sync operation ${op.id}: ${op.url}`);
          await this.localDb.syncQueue.delete(op.id!);
          await this.updatePendingCount();
          continue;
        }

        try {
          let response: any;
          if (op.method === 'POST') {
            // If this Form 4473 was saved offline without a transfer number,
            // generate one now (chronological order is guaranteed by orderBy timestamp).
            if (op.needsTransactionId) {
              const maxRes = await firstValueFrom(
                this.http.get<{ message: string }>(`${this.apiUrl}/bound_books/max-d-form4473`)
              ).catch(() => ({ message: '0' }));
              const nextId = (+(maxRes?.message ?? '0') + 1).toString();
              op.payload = { ...op.payload, transaction_serial_number: nextId };
              await this.localDb.syncQueue.update(op.id!, { payload: op.payload });
            }
            response = await firstValueFrom(this.http.post(op.url, op.payload));
          } else if (op.method === 'PUT') {
            response = await firstValueFrom(this.http.put(op.url, op.payload));
          } else if (op.method === 'PATCH') {
            response = await firstValueFrom(this.http.patch(op.url, op.payload));
          } else if (op.method === 'DELETE') {
            response = await firstValueFrom(this.http.delete(op.url));
          }

          // After a successful POST with a temp ID, rewrite any later ops in this
          // batch that still reference the old temp URL (e.g. PUT /resource/tempUUID).
          if (op.method === 'POST' && op.tempId) {
            const later = pendingOps.slice(i + 1);
            await Promise.all(this.reconcileTempId(op.tempId, response, later));
          }
          
          // Remove from queue on success
          await this.localDb.syncQueue.delete(op.id!);
          await this.updatePendingCount();
        } catch (err: any) {
          console.error(`Failed to sync operation ${op.id}`, err);

          // DELETE 404 means the record is already gone on server.
          // Treat as success to unblock and clean stale queue entries.
          if (
            err instanceof HttpErrorResponse &&
            err.status === 404 &&
            op.method === 'DELETE'
          ) {
            console.warn(`DELETE target already missing for ${op.url} — removing queued op.`);
            await this.localDb.syncQueue.delete(op.id!);
            await this.updatePendingCount();
            continue;
          }

          // 401/403 = session expired — halt sync entirely to preserve the queue.
          // Do NOT retry or discard; the user must re-authenticate first.
          if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
            console.warn(`Sync halted — session expired (${err.status}). Queue preserved.`);
            this.authError$.next();
            return; // exit syncPendingOperations, queue untouched
          }

          // 409 Conflict = server has a newer version (LWW rejection).
          // Do NOT retry — discard immediately and notify the user.
          if (err instanceof HttpErrorResponse && err.status === 409) {
            console.warn(`Conflict on ${op.method} ${op.url} — discarding queued change (server wins).`);
            await this.localDb.syncQueue.delete(op.id!);
            await this.updatePendingCount();
            this.conflictErrors$.next({
              url: op.url,
              method: op.method,
              message: err.error?.message || 'A newer version of this record exists on the server. Your offline change was discarded.',
            });
            continue;
          }

          // 400/500 — retry up to 5 times then discard to unblock the queue.
          if (op.retryCount < 5) {
            await this.localDb.syncQueue.update(op.id!, { retryCount: op.retryCount + 1 });
          } else {
            console.warn(`Giving up on sync operation ${op.id} after maximum retries`);
            await this.localDb.syncQueue.delete(op.id!);
            await this.updatePendingCount();
          }
        }
      }
    } finally {
      this.isSyncing$.next(false);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
